// client/src/components/public/PublicActionsPage.jsx
//
// /hub/:tenantSlug/actions — every open or opening-soon raffle and auction
// across the tenant's public events, so a visitor can browse them in one
// place and enter / bid on several. No auth, no app chrome; wears the hub's
// colours. Opt-in: 404s ("not available") unless the tenant's public page is
// published with "Raffles & auctions" ticked (hub/HubSettingsPage.jsx).
// Data: GET /api/public/tenants/:slug/actions. Crawler meta for this URL is
// injected by the server (routes/publicPageRoutes.js).

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchPublicActions } from '../../services/hubApi';
import { assetUrl } from '../../utils/assetUrl';
import PublicActionCard from './PublicActionCard';
import './PublicHub.css';

const FILTERS = ['all', 'raffle', 'auction'];

export default function PublicActionsPage() {
  const { t } = useTranslation();
  const { tenantSlug } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');
  const [filter, setFilter] = useState('all');
  const [now, setNow] = useState(() => Date.now());

  // A public page must not wear the app theme (see PublicEventPage).
  useEffect(() => {
    const html = document.documentElement;
    const removed = [...html.classList].filter((c) => c.startsWith('theme-'));
    removed.forEach((c) => html.classList.remove(c));
    return () => removed.forEach((c) => html.classList.add(c));
  }, []);

  useEffect(() => {
    let alive = true;
    setState('loading');
    fetchPublicActions(tenantSlug)
      .then((d) => {
        if (!alive) return;
        setData(d);
        setState('ready');
        document.title = t('hub.actions.docTitle', 'Raffles & auctions | {{name}}', { name: d.tenant?.name || '' });
      })
      .catch((err) => { if (alive) setState(err.status === 404 ? 'not-found' : 'error'); });
    return () => { alive = false; };
  }, [tenantSlug, t]);

  // "Closes in …" stays honest while the page is left open.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const actions = useMemo(() => data?.actions || [], [data]);
  const counts = useMemo(() => ({
    all: actions.length,
    raffle: actions.filter((a) => a.kind === 'raffle').length,
    auction: actions.filter((a) => a.kind === 'auction').length,
  }), [actions]);
  const shown = filter === 'all' ? actions : actions.filter((a) => a.kind === filter);
  const open = shown.filter((a) => a.state === 'open');
  const upcoming = shown.filter((a) => a.state === 'upcoming');

  if (state !== 'ready') {
    const msg = {
      loading: t('hub.public.loading', 'Loading…'),
      'not-found': t('hub.public.notFound', "This page isn't available."),
      error: t('hub.public.loadError', 'Something went wrong loading this page.'),
    }[state];
    return (
      <div className="hub-page">
        <div className="hub-inner"><p className="hub-status" role={state === 'error' ? 'alert' : undefined}>{msg}</p></div>
      </div>
    );
  }

  const { tenant, hub } = data;
  const tz = tenant.timezone;
  const style = { '--hub-accent': hub.theme.accent, '--hub-accent-text': hub.theme.accentText };
  const filterLabel = (k) => ({
    all: t('hub.actions.filterAll', 'All'),
    raffle: t('hub.actions.filterRaffles', 'Raffles'),
    auction: t('hub.actions.filterAuctions', 'Auctions'),
  }[k]);

  return (
    <div className={`hub-page${hub.theme.mode === 'dark' ? ' dark' : ''}`} style={style}>
      <div className="hub-inner hub-inner-wide">
        <header className="hub-head hub-head-row">
          {tenant.logo && <img src={assetUrl(tenant.logo)} alt="" />}
          <div>
            <Link className="hub-back" to={`/hub/${encodeURIComponent(tenant.slug)}`}>
              {t('hub.actions.back', '← {{name}}', { name: tenant.name })}
            </Link>
            <h1>{t('hub.actions.title', 'Raffles & auctions')}</h1>
            <p>{t('hub.actions.intro', 'Everything {{name}} has open right now. Enter as many as you like.', { name: tenant.name })}</p>
          </div>
        </header>

        {counts.raffle > 0 && counts.auction > 0 && (
          <div className="hub-filters" role="group" aria-label={t('hub.actions.filterLabel', 'Show')}>
            {FILTERS.map((k) => (
              <button key={k} type="button" className="hub-filter" aria-pressed={filter === k} onClick={() => setFilter(k)}>
                {filterLabel(k)} <span className="hub-filter-n">{counts[k]}</span>
              </button>
            ))}
          </div>
        )}

        {actions.length === 0 && (
          <p className="hub-empty">{t('hub.actions.empty', 'No raffles or auctions are open right now. Check back soon.')}</p>
        )}

        {open.length > 0 && (
          <section className="hub-section" aria-labelledby="act-open">
            <h2 id="act-open">{t('hub.actions.openNowHeading', 'Open now')}</h2>
            <div className="hub-act-grid">
              {open.map((a) => <PublicActionCard key={a.id} action={a} now={now} timeZone={tz} />)}
            </div>
          </section>
        )}

        {upcoming.length > 0 && (
          <section className="hub-section" aria-labelledby="act-upcoming">
            <h2 id="act-upcoming">{t('hub.actions.upcomingHeading', 'Opening soon')}</h2>
            <div className="hub-act-grid">
              {upcoming.map((a) => <PublicActionCard key={a.id} action={a} now={now} timeZone={tz} />)}
            </div>
          </section>
        )}

        <p className="hub-foot">{t('hub.public.poweredBy', 'Powered by On-Screen')}</p>
      </div>
    </div>
  );
}
